import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate, periodLabel } from '@/lib/format';
import { Banknote, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function Collections() {
  const [demands, setDemands] = useState([]);
  const [period, setPeriod] = useState(currentPeriod());
  const [statusFilter, setStatusFilter] = useState('all');
  const [payOpen, setPayOpen] = useState(null);
  const [pay, setPay] = useState({ amount: 0, mode: 'upi', reference: '', payment_date: new Date().toISOString().slice(0, 10), note: '' });
  const [receipt, setReceipt] = useState(null);

  const load = () => api.get('/rent-demands', { params: { period: period || undefined } }).then(r => setDemands(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period]);

  const generate = async () => {
    try {
      const { data } = await api.post('/rent-demands/generate', null, { params: { period } });
      toast.success(`Generated ${data.created} demands (${data.skipped} skipped)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const recordPay = async () => {
    try {
      const { data } = await api.post('/payments', { demand_id: payOpen.id, ...pay, amount: Number(pay.amount) });
      toast.success(`Receipt ${data.receipt_number} created`);
      setPayOpen(null); load();
      const r = await api.get(`/payments/${data.id}/receipt`);
      setReceipt(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filtered = statusFilter === 'all' ? demands : demands.filter(d => d.status === statusFilter);

  return (
    <div>
      <PageHeader title="Collections" subtitle="Generate rent demands and record payments" actions={
        <div className="flex items-center gap-2">
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="period-input" />
          <Button onClick={generate} data-testid="generate-demands-button"><Sparkles className="h-4 w-4 mr-1" /> Generate for {periodLabel(period)}</Button>
        </div>
      } />

      <div className="flex items-center gap-3 mb-3">
        <div className="text-xs text-muted-foreground">Filter:</div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="collections-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="due">Due</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60"><tr>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Tenant</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Property / Unit</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Period</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Due</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Amount</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Outstanding</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Status</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Action</th>
          </tr></thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 font-medium">{d.tenant_name}</td>
                <td className="px-3 py-2">{d.property_name} <span className="text-muted-foreground">/</span> {d.unit_number}</td>
                <td className="px-3 py-2">{periodLabel(d.period)}</td>
                <td className="px-3 py-2">{fmtDate(d.due_date)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(d.amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR(d.outstanding)}</td>
                <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                <td className="px-3 py-2 text-right">
                  {d.outstanding > 0 ? (
                    <Button size="sm" data-testid={`record-payment-button-${d.id}`} onClick={() => { setPayOpen(d); setPay({ amount: d.outstanding, mode: 'upi', reference: '', payment_date: new Date().toISOString().slice(0, 10), note: '' }); }}>
                      <Banknote className="h-3.5 w-3.5 mr-1" /> Record
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">–</span>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No demands. Generate them above.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          {payOpen && (
            <div className="text-sm space-y-3">
              <div className="text-muted-foreground">{payOpen.tenant_name} · {periodLabel(payOpen.period)} · outstanding {formatINR(payOpen.outstanding)}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                <div><Label>Date</Label><Input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></div>
                <div><Label>Mode</Label><Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent></Select></div>
                <div><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="UTR / Txn ID" /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={recordPay} data-testid="record-payment-submit">Save payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" /> Receipt</DialogTitle></DialogHeader>
          {receipt && (
            <div className="text-sm border rounded-lg p-4 space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Receipt #</span><span className="font-mono">{receipt.payment.receipt_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{fmtDate(receipt.payment.payment_date)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tenant</span><span>{receipt.tenant?.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Property</span><span>{receipt.property?.name} · {receipt.unit?.unit_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="uppercase">{receipt.payment.mode}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-mono text-xs">{receipt.payment.reference || '—'}</span></div>
              <div className="border-t pt-2 mt-2 flex justify-between text-base font-semibold"><span>Amount</span><span className="tabular-nums">{formatINR(receipt.payment.amount)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
